import React, { createContext, useContext, useState, useEffect } from 'react';
import AuthContextType from './AuthContextType';
import UserClaims from './UserClaims';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const [user, setUser] = useState<UserClaims | null>(null);

  const login = (token: string) => {
    const claims = parseJWT(token);
    if (isTokenExpired(claims)) {
      logout();
      return;
    }

    localStorage.setItem('token', token);
    setUser(claims);
    setIsAuthenticated(true);
    setIsAuthInitialized(true);
  };

  const logout = () => {
    localStorage.removeItem('token'); 
    setUser(null);
    setIsAuthenticated(false);
    setIsAuthInitialized(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const claims = parseJWT(token);
        if (isTokenExpired(claims)) {
          logout();
          return;
        }

        setUser(claims);
        setIsAuthenticated(true);
        setIsAuthInitialized(true);
      } catch {
        logout();
      }
    } else {
      setIsAuthInitialized(true);
    }

    const handleAuthExpired = () => logout();
    window.addEventListener('readerly:auth-expired', handleAuthExpired);
    return () => window.removeEventListener('readerly:auth-expired', handleAuthExpired);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isAuthInitialized, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const base64UrlToBase64 = (base64Url: string): string => {
  return base64Url.replace(/-/g, '+').replace(/_/g, '/').padEnd(base64Url.length + (4 - base64Url.length % 4) % 4, '=');
};

const parseJWT = (token: string): UserClaims => {
  try {
      const payloadBase64Url = token.split('.')[1];
      const payloadBase64 = base64UrlToBase64(payloadBase64Url);

      const decodedPayload = new TextDecoder().decode(Uint8Array.from(atob(payloadBase64), c => c.charCodeAt(0)));
      const decoded = JSON.parse(decodedPayload) as Record<string, any>;

      return {
          id: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
          name: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
          email: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"],
          role: decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"],
          isSubscribed: decoded.IsSubscribed === 'true',
          exp: typeof decoded.exp === 'number' ? decoded.exp : Number(decoded.exp)
      };
  } catch (error) {
      console.error('Invalid token:', error);
      throw new Error('Token is invalid');
  }
};

const isTokenExpired = (claims: UserClaims): boolean => {
  if (!claims.exp || !Number.isFinite(claims.exp)) return true;
  return claims.exp * 1000 <= Date.now();
};



export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
